import React, { useState, useEffect, useCallback } from 'react';
import { getCacheStats, getPendingOps, removePendingOp, syncPendingOps, clearAllCache } from '../services/offlineDB';

const SyncPanelPage = () => {
  const [stats, setStats] = useState(null);
  const [pendingOps, setPendingOps] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState('');

  const loadData = useCallback(async () => {
    try {
      const s = await getCacheStats();
      setStats(s);
      const ops = await getPendingOps();
      setPendingOps(ops || []);
    } catch (e) {
      console.warn('Error loading sync data:', e);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => { setIsOnline(true); loadData(); };
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const onSync = () => loadData();
    window.addEventListener('tkorvex-sync-complete', onSync);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); window.removeEventListener('tkorvex-sync-complete', onSync); };
  }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    setMessage('');
    try {
      const ops = await getPendingOps();
      if (!ops || ops.length === 0) {
        setMessage('No hay operaciones pendientes');
        return;
      }
      const api = (await import('../services/api')).default;
      const r = await syncPendingOps(api);
      if (r.failed === 0) setMessage(`${r.synced} operaciones sincronizadas exitosamente`);
      else setMessage(`${r.synced} sincronizadas, ${r.failed} fallaron (se reintentaran; las marcadas en rojo requieren revision)`);
      if (r.synced > 0) window.dispatchEvent(new CustomEvent('tkorvex-sync-complete', { detail: r }));
      await loadData();
    } catch (e) {
      setMessage('Error durante la sincronizacion');
    } finally { setSyncing(false); }
  };

  const handleDiscard = async (op) => {
    if (!window.confirm('Descartar esta operacion? El dato no se enviara al servidor.')) return;
    await removePendingOp(op.id);
    await loadData();
  };

  const handleClearCache = async () => {
    if (!window.confirm('Limpiar todo el cache local? Los datos no sincronizados se perderan.')) return;
    try {
      await clearAllCache();
      setMessage('Cache limpiado. Usa "Preparar Modo Campo" para descargar datos nuevamente.');
      await loadData();
    } catch { setMessage('Error limpiando cache'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6" data-testid="sync-panel-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Panel de Sincronizacion</h2>
        <p className="text-sm text-gray-500 mt-1">Estado de datos offline, operaciones pendientes y cache local</p>
      </div>

      {/* Connection Status */}
      <div className={`rounded-xl p-4 flex items-center gap-3 ${isOnline ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="connection-status">
        <span className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
        <span className={`font-semibold text-sm ${isOnline ? 'text-green-800' : 'text-amber-800'}`}>
          {isOnline ? 'Conectado a internet' : 'MODO CAMPO — Sin conexion'}
        </span>
      </div>

      {message && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700" data-testid="sync-message">
          {message}
        </div>
      )}

      {/* Pending Operations */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Operaciones Pendientes</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-bold ${pendingOps.length > 0 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-testid="pending-count">
            {pendingOps.length}
          </span>
        </div>

        {pendingOps.length === 0 ? (
          <p className="text-sm text-gray-500">No hay operaciones pendientes. Todo esta sincronizado.</p>
        ) : (
          <>
            <div className="overflow-x-auto max-h-60 overflow-y-auto mb-4">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Tipo</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Endpoint</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Fecha</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Datos</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOps.map((op, i) => (
                    <tr key={i} className={`border-b border-gray-100 ${op.failed ? 'bg-red-50' : ''}`} data-testid={`pending-op-${i}`}>
                      <td className="py-2 px-3">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono">{op.method}</span>
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-600">{op.url}</td>
                      <td className="py-2 px-3 text-gray-500">{op.timestamp ? new Date(op.timestamp).toLocaleString('es') : '--'}</td>
                      <td className="py-2 px-3 text-gray-500 truncate max-w-[200px]">{JSON.stringify(op.data).slice(0, 60)}...</td>
                      <td className="py-2 px-3">
                        {op.failed ? (
                          <div className="flex items-center gap-2">
                            <span className="text-red-700 text-xs" title={op.lastError}>Error: {(op.lastError || '').slice(0, 50)}</span>
                            <button onClick={() => handleDiscard(op)} className="px-2 py-0.5 border border-red-300 text-red-600 rounded text-xs hover:bg-red-100" data-testid={`discard-op-${i}`}>Descartar</button>
                          </div>
                        ) : op.lastError ? (
                          <span className="text-amber-700 text-xs" title={op.lastError}>Reintento {op.retries}/5</span>
                        ) : (
                          <span className="text-gray-400 text-xs">Pendiente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={handleSync}
              disabled={syncing || !isOnline}
              className="w-full py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50 transition"
              data-testid="sync-now-btn"
            >
              {syncing ? 'Sincronizando...' : !isOnline ? 'Sin conexion — No se puede sincronizar' : `Sincronizar ${pendingOps.length} operaciones`}
            </button>
          </>
        )}
      </div>

      {/* Cache Stats */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Cache Local (Modo Campo)</h3>
        {stats ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{stats.animalesCached || 0}</p>
              <p className="text-xs text-gray-500">Animales</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{stats.pesajesCached || 0}</p>
              <p className="text-xs text-gray-500">Pesajes</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{stats.serviciosCached || 0}</p>
              <p className="text-xs text-gray-500">Servicios</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{stats.paricionesCached || 0}</p>
              <p className="text-xs text-gray-500">Pariciones</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No hay datos en cache. Usa "Preparar Modo Campo" desde el Dashboard.</p>
        )}

        {stats?.lastCacheTime && (
          <p className="text-xs text-gray-400 mt-3">
            Ultima descarga: {new Date(stats.lastCacheTime).toLocaleString('es')}
          </p>
        )}

        <div className="mt-4 flex gap-3">
          <button
            onClick={handleClearCache}
            className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
            data-testid="clear-cache-btn"
          >
            Limpiar Cache
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
        <h4 className="font-semibold text-gray-700 mb-2">Como funciona la sincronizacion</h4>
        <ul className="text-sm text-gray-600 space-y-1.5 list-disc list-inside">
          <li>Cuando trabajas sin internet, las operaciones se guardan localmente</li>
          <li>Al recuperar conexion, podes sincronizar manualmente desde aca</li>
          <li>Las operaciones se envian al servidor en el mismo orden que fueron creadas</li>
          <li>Si una operacion falla, se reintenta en la proxima sincronizacion</li>
          <li>Usa "Preparar Modo Campo" en el Dashboard para descargar datos antes de ir al campo</li>
        </ul>
      </div>
    </div>
  );
};

export default SyncPanelPage;
